import Typography from '@mui/material/Typography';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useCompanyCore } from '../../../../core/modules/company';
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { useBusinessCore } from '../../../../core/modules/business';
import { useArticlesCore } from '../../../../core/modules/articles';
import { useContainerCore } from '../../../../core/modules/container';
import { GetClassifiedArticleParams } from '../../../../core/modules/articles/interfaces/get-classified-articles';
import { BusinessArea } from '../../../../core/interfaces/business.interface';

const schema = yup.object().shape({
  code: yup.string().optional(),
  description: yup.string().optional(),
  reference: yup.string().optional(),
  provider_id: yup.number().optional(),
  container_id: yup.number().optional(),
});

const Header = () => {
  const { companyId } = useCompanyCore();
  const { businesses } = useBusinessCore();
  const { getClassifiedArticles, clearArticles } = useArticlesCore();
  const { getContainers } = useContainerCore();

  const { control, formState, handleSubmit, watch, setValue } =
    useForm<GetClassifiedArticleParams>({
      mode: 'onChange',
      resolver: yupResolver(schema),
    });
  const { isValid } = formState;

  const [loading, setLoading] = useState(false);
  const [providerAreas, setProviderAreas] = useState<BusinessArea[]>([]);
  const [containers, setContainers] = useState<any[]>([]);

  // Observar cambios en el proveedor seleccionado
  const watchedProviderId = watch('provider_id');

  const onSubmit = useCallback(
    (values: GetClassifiedArticleParams) => {
      console.log('Valores enviados en el filtro:', values);
      if (
        !values.code &&
        !values.description &&
        !values.provider_id &&
        !values.reference &&
        !values.container_id
      ) {
        clearArticles();
        return null;
      }

      if (companyId) {
        setLoading(true);
        getClassifiedArticles(
          companyId,
          values,
          () => setLoading(false),
          () => setLoading(false)
        );
      }
    },
    [clearArticles, companyId, getClassifiedArticles]
  );

  const providers = useMemo(() => {
    return businesses.filter((business) => business.is_provider);
  }, [businesses]);

  // Efecto para actualizar las áreas cuando cambie el proveedor
  useEffect(() => {
    if (watchedProviderId && watchedProviderId !== 0) {
      const selectedProvider = providers.find(
        (p) => p.id === watchedProviderId
      );
      if (selectedProvider && selectedProvider.areas) {
        setProviderAreas(selectedProvider.areas);
      } else {
        setProviderAreas([]);
      }
    } else {
      setProviderAreas([]);
    }
  }, [watchedProviderId, providers]);

  // Efecto para cargar contenedores cuando cambie la compañía
  useEffect(() => {
    if (companyId) {
      getContainers(
        companyId,
        { page: 1 },
        (containers) => setContainers(containers),
        () => setContainers([])
      );
    }
  }, [companyId, getContainers]);

  return (
    <div className="p-24 sm:p-32 w-full border-b-1 bg-white header-components">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex flex-col items-center sm:items-start">
          <Typography className="text-24 md:text-32 font-extrabold tracking-tight leading-none">
            Consulta Artículo
          </Typography>
        </div>
      </div>
      <form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        className="flex w-full mt-20"
      >
        <div className="flex w-full px-20">
          <div className="w-full">
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="SKU"
                  size="small"
                  variant="outlined"
                  disabled={!companyId}
                  fullWidth
                />
              )}
            />
          </div>
          <div className="w-full">
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Descripción"
                  size="small"
                  variant="outlined"
                  disabled={!companyId}
                  fullWidth
                />
              )}
            />
          </div>
          <div className="w-full">
            <Controller
              name="reference"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Referencia"
                  size="small"
                  variant="outlined"
                  disabled={!companyId}
                  fullWidth
                />
              )}
            />
          </div>
          <div className="w-full">
            <Controller
              name="provider_id"
              control={control}
              render={({ field }) => (
                <FormControl className="max-w-[320px]" size="small" fullWidth>
                  <InputLabel id="provider-label">Proveedor</InputLabel>
                  <Select
                    {...field}
                    labelId="provider-label"
                    label="Proveedor"
                    size="small"
                    disabled={loading || !companyId}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                  >
                    <MenuItem value={0}>Seleccionar</MenuItem>
                    {providers.map((provider) => (
                      <MenuItem key={provider.id} value={provider.id}>
                        {provider.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </div>
          <div className="w-full">
            <Controller
              name="provider_id"
              control={control}
              render={({ field }) => (
                <FormControl className="max-w-[320px]" size="small" fullWidth>
                  <InputLabel id="provider-area-label">
                    Área del Proveedor
                  </InputLabel>
                  <Select
                    {...field}
                    labelId="provider-area-label"
                    label="Área del Proveedor"
                    size="small"
                    disabled={
                      loading ||
                      !companyId ||
                      !watchedProviderId ||
                      watchedProviderId === 0 ||
                      providerAreas.length === 0
                    }
                    onChange={(e) => {
                      console.log('Área seleccionada:', e.target.value);
                      field.onChange(e.target.value);
                    }}
                  >
                    <MenuItem value="">Seleccionar</MenuItem>
                    {providerAreas.map((area) => (
                      <MenuItem key={area.id} value={area.id}>
                        {area.acronym}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </div>
          <div className="w-full">
            <Controller
              name="container_id"
              control={control}
              render={({ field }) => (
                <FormControl className="max-w-[320px]" size="small" fullWidth>
                  <InputLabel id="container-label">Contenedor de Origen</InputLabel>
                  <Select
                    {...field}
                    labelId="container-label"
                    label="Contenedor de Origen"
                    size="small"
                    disabled={loading || !companyId}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                  >
                    <MenuItem value={0}>Seleccionar</MenuItem>
                    {containers.map((container) => (
                      <MenuItem key={container.id} value={container.id}>
                        {container.code} - {container.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </div>
        </div>
        <Button
          variant="contained"
          color="secondary"
          className="mx-10"
          type="submit"
          disabled={!isValid || loading || !companyId}
        >
          <SearchOutlinedIcon />
          <span className="mx-8">Buscar</span>
        </Button>
      </form>
    </div>
  );
};

export default Header;
